/**
 * Peony tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const peonyTattoosPack: ToilePack = {
    slug: 'peony-tattoos',
    category: 'tattoo',
    name: 'Peony tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'japanese', label: 'Japanese peony', subject: 'a large peony in the irezumi manner with curling leaves, set against wind bars and a crest of waves'},
        {key: 'bloom', label: 'Single bloom', subject: 'a single fully open peony seen from above, layered ruffled petals around a tight centre'},
        {key: 'bouquet', label: 'Peony bouquet', subject: 'three peonies of different sizes on stems with two closed buds and leaves, gathered at the base'},
    ],
    styles: [
        {key: 'irezumi', label: 'Irezumi', style: 'a Japanese irezumi tattoo drawing, confident black outlines, bokashi gradient shading and flat colour fields', palette: 'red, teal and ochre accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin'},
    ],
    directions: ['a koi', 'pink ink', 'ribs'],
    directionHint: 'try: a koi',
    seo: {
        title: 'Peony tattoos: irezumi, single bloom, bouquet',
        metaDescription: 'Nine peony tattoo designs: an irezumi peony in wind and waves, one open bloom and a bouquet with buds, in three styles.',
        h1: 'Peony tattoo designs: irezumi, a single bloom, a bouquet',
        tagline: 'Three designs, each drawn in irezumi, fine line and dotwork on white paper.',
        intro: 'The peony is the king of flowers in Japanese tattooing and a favourite of fine-line work. These nine show an irezumi peony in wind and waves, a single open bloom seen from above, and a bouquet of three peonies with buds. Each is drawn in irezumi, fine line and dotwork. Type a different flower or a twist, such as a koi beside the bloom, and the three styles carry the request.',
        useCases: [
            'a thigh, shoulder or rib design to take to an artist',
            'a floral print for a bedroom wall',
            'a phone wallpaper',
            'a card or invitation motif',
        ],
        faq: [
            {q: 'What are the three designs?', a: 'An irezumi peony in wind and waves, a single open bloom, and a bouquet of three peonies with buds. Each comes in three styles.'},
            {q: 'Can I print these?', a: 'Each design sits on a plain white background in dark line work, so it reads clearly on paper.'},
            {q: 'What is the difference between the three styles?', a: 'Irezumi has bold outlines, gradient shading and red, teal and ochre accents. Fine line is one hairline stroke with almost no shading, and dotwork builds its shading from dots in black ink only.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type the subject or twist you want, such as a koi beside the bloom. The nine shown are samples, and a request is not a promised result.'},
        ],
    },
}
